import { MigrationError, migrateDocument, type Migration, MIGRATIONS } from './migrations/index.js';
import { validateDocument, type ValidationIssue } from './validation.js';
import type { ProjectDocument } from './types.js';
import { modelTexts } from './texts.js';

export type ParseResult =
  | { readonly ok: true; readonly document: ProjectDocument; readonly migratedFrom: number | null }
  | { readonly ok: false; readonly error: string; readonly issues: readonly ValidationIssue[] };

/**
 * The single entry point for untrusted project data (server payloads, IndexedDB drafts,
 * imported files): migrate to the current schema, then validate.
 */
export function parseProjectDocument(
  raw: unknown,
  migrations: readonly Migration[] = MIGRATIONS,
): ParseResult {
  let migrated;
  try {
    migrated = migrateDocument(raw, migrations);
  } catch (err) {
    if (err instanceof MigrationError) return { ok: false, error: err.message, issues: [] };
    throw err;
  }
  const result = validateDocument(migrated.document);
  if (!result.ok) {
    return { ok: false, error: modelTexts().parse.invalidData, issues: result.issues };
  }
  const migratedFrom = migrated.applied.length > 0 ? migrated.fromVersion : null;
  return { ok: true, document: result.document, migratedFrom };
}

export function parseProjectJson(json: string): ParseResult {
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return { ok: false, error: modelTexts().parse.invalidJson, issues: [] };
  }
  return parseProjectDocument(raw);
}

/** Serializes after validation so an invalid in-memory state can never be persisted. */
export function serializeProjectDocument(doc: ProjectDocument): string {
  const result = validateDocument(doc);
  if (!result.ok) {
    const first = result.issues[0];
    throw new Error(
      `Ungültiges Projekt: ${first ? `${first.path}: ${first.message}` : 'unbekannt'}`,
    );
  }
  return JSON.stringify(result.document);
}
